import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Paper,
    IconButton,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();

const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

export default function SubjectsPage() {
    const confirm = useConfirm();
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = () => {
        setLoading(true);
        axios
            .get(`${API_URL}/api/subjects`)
            .then((res) => setSubjects(res.data || []))
            .catch(() => setSubjects([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const create = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setBusy(true);
        setError('');
        try {
            await axios.post(`${API_URL}/api/subjects`, { name: newName.trim() }, authHeaders());
            setNewName('');
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add.');
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (subject) => {
        try {
            await axios.patch(
                `${API_URL}/api/subjects/${subject._id}`,
                { active: !subject.active },
                authHeaders()
            );
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete subject', message: 'Delete this subject?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/subjects/${id}`, authHeaders());
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete.');
        }
    };

    return (
        <Container>
            <Typography variant="h4" sx={{ mb: 1 }}>
                Subjects
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
                These subjects power the dropdowns that <strong>parents, students, and teachers</strong>
                {' '}see when they fill in or edit their profile (e.g. a teacher picks which subjects + levels
                they teach; a parent picks the subject their child is studying). They also drive the
                <strong> Parents</strong> and <strong>Students</strong> filters on this admin panel.
                Toggle a subject off to hide it from new profile forms without losing existing data;
                delete it to remove it entirely.
                <br />
                <em>
                    Note: the public Contact Us form uses a fixed list (English Language / English Literature / Both)
                    per the brief, not this list.
                </em>
            </Typography>
                    <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Subjects are the master list used throughout the system — as chips on class cards, as filters, as dropdown options for tutors and parents.<br />
                            <strong>2.</strong> Click <strong>New subject</strong> to add one. Deactivating a subject hides it from new dropdowns but doesn't affect existing classes/tutors that already use it.<br />
                            <strong>3.</strong> Renaming a subject flows through everywhere immediately — do it once, no other edits needed.<br />
                        </Typography>
                    </HowItWorksCard>

            <Card sx={{ p: 3, mb: 3 }}>
                <Box component="form" onSubmit={create} sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="New subject"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <Button type="submit" variant="contained" disabled={busy}>
                        Add
                    </Button>
                </Box>
                {error && (
                    <Typography color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subjects.map((s) => (
                                <TableRow key={s._id}>
                                    <TableCell>{s.name}</TableCell>
                                    <TableCell>
                                        <Switch checked={s.active} onChange={() => toggleActive(s)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => remove(s._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {subjects.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No subjects yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Container>
    );
}